import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button, Modal, Textarea, toast } from '../../components/ui';
import { formatMoney } from '../../lib/format';
import { useTimesheetAction, type ApprovalRow } from './api';
import { formatHoursUnit, type HoursFormat } from './hours';

/**
 * Approve a batch of submitted timesheets in one action.
 *
 * A timesheet is a PERIOD (a day, a week or a month, per the person's
 * frequency), so a batch can mix kinds — twenty daily sheets and one month.
 * Each is addressed by its stored id. Same transition, same server rule
 * (`can.approve` is decided per row on the SERVER, and only rows carrying it
 * can ever be selected), applied to a set.
 *
 * ⚠ **Sequential, and honest about partial failure.** The calls go one at a
 * time and each result is recorded: a week that was withdrawn, resubmitted or
 * approved by somebody else since the page loaded comes back 409, and that must
 * be reported by name rather than swallowed into a green toast. Approving ten
 * weeks and silently missing one is worse than approving none.
 */

export interface BulkApproveDialogProps {
  open: boolean;
  rows: ApprovalRow[];
  hoursFormat: HoursFormat;
  onClose: () => void;
  /** Fired once the run finishes, whatever the outcome (clear the selection). */
  onDone?: (approved: number) => void;
}

interface Failure {
  name: string;
  reason: string;
}

export function BulkApproveDialog({
  open,
  rows,
  hoursFormat,
  onClose,
  onDone,
}: BulkApproveDialogProps) {
  const [note, setNote] = useState('');
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState<{ approved: number; failures: Failure[] } | null>(null);
  const approve = useTimesheetAction('approve');

  useEffect(() => {
    if (open) {
      setNote('');
      setDone(null);
      setRunning(false);
    }
  }, [open]);

  if (!open) return null;

  const hours = Math.round(rows.reduce((s, r) => s + r.hours, 0) * 100) / 100;
  const cost = rows.reduce((s, r) => s + (r.cost ?? 0), 0);
  // Salaried, or hours with no rate: left out of the total, and counted.
  const uncosted = rows.filter((r) => r.costBasis !== 'hourly' || r.uncostedHours > 0).length;

  const run = async () => {
    setRunning(true);
    const trimmed = note.trim();
    const failures: Failure[] = [];
    let approved = 0;
    for (const row of rows) {
      try {
        await approve.mutateAsync({
          timesheetId: row.id,
          note: trimmed ? trimmed : null,
        });
        approved += 1;
      } catch (err) {
        failures.push({
          name: `${row.person.name} · ${row.label}`,
          reason: err instanceof Error ? err.message : 'Refused by the server.',
        });
      }
    }
    setRunning(false);
    setDone({ approved, failures });
    if (failures.length === 0) {
      toast.success(
        `${approved} timesheet${approved === 1 ? '' : 's'} approved.`,
      );
      onClose();
      onDone?.(approved);
    }
    // With failures the dialog STAYS OPEN holding the list — a toast that
    // disappears in four seconds is not where you report which weeks did not go
    // through.
  };

  if (done && done.failures.length > 0) {
    return (
      <Modal
        open
        onOpenChange={(next) => {
          if (!next) {
            onClose();
            onDone?.(done.approved);
          }
        }}
        title="Some timesheets were not approved"
        footer={
          <Button
            onClick={() => {
              onClose();
              onDone?.(done.approved);
            }}
          >
            Close
          </Button>
        }
      >
        <div className="space-y-3">
          {done.approved > 0 && (
            <p className="flex items-center gap-2 text-body-sm font-semibold text-success">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
              {done.approved} timesheet{done.approved === 1 ? '' : 's'} approved.
            </p>
          )}
          <ul className="space-y-2">
            {done.failures.map((failure) => (
              <li
                key={failure.name}
                className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2"
              >
                <AlertCircle className="mt-px h-4 w-4 shrink-0 text-status-partial" aria-hidden />
                <p className="text-body-sm leading-snug text-grey-700">
                  <span className="font-bold text-secondary">{failure.name}</span> — {failure.reason}
                </p>
              </li>
            ))}
          </ul>
          <p className="text-body-sm text-grey-500">
            They are still in the queue with an Approve button — each most likely moved while this
            page was open.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && !running && onClose()}
      title={`Approve ${rows.length} timesheet${rows.length === 1 ? '' : 's'}`}
      description={
        <>
          <span className="num font-semibold text-secondary">
            {formatHoursUnit(hours, hoursFormat)}
          </span>
          {cost > 0 && (
            <>
              {' · '}
              <span className="num font-semibold text-secondary">
                {formatMoney(cost, { whole: true })}
              </span>{' '}
              cost
            </>
          )}
          {uncosted > 0 && (
            <>
              {' · '}
              {uncosted} not costed
            </>
          )}
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button onClick={run} loading={running}>
            Approve {rows.length}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-body-sm text-grey-500">
          Each stays read-only until someone reopens it. Approving does not bill anything —
          invoicing is separate.
        </p>
        {/* Who is in the batch, by name. A count alone is not a thing anyone can
            check before signing off ten people's hours. */}
        <ul className="max-h-48 divide-y divide-grey-100 overflow-y-auto rounded-lg border border-grey-200">
          {rows.map((row) => (
            <li key={row.id ?? row.person.id} className="flex items-baseline gap-2 px-3 py-1.5 text-body-sm">
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold text-secondary">{row.person.name}</span>
                <span className="text-grey-500"> · {row.label}</span>
              </span>
              <span className="num shrink-0 text-grey-500">
                {formatHoursUnit(row.hours, hoursFormat)}
              </span>
            </li>
          ))}
        </ul>
        <Textarea
          label="Note on all of them (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Approved for the September payroll run"
          textareaClassName="resize-none"
          disabled={running}
        />
      </div>
    </Modal>
  );
}
